import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Home</Link>
        
        <h1 className="legal-title">DashFox One Online Customer Agreement</h1>
        <p className="legal-effective-date">Effective as of January 01, 2024</p>

        <div className="legal-content">
          <p>This DashFox One Online Customer Agreement ("Agreement") is entered into by and between DashFox One, Inc. ("DashFox One," "we," "us," or "our") and the entity or person placing an order for or accessing the Service ("Customer," "you," or "your"). This Agreement consists of the terms and conditions set forth below and any DashFox One ordering documents, online registration, order descriptions or order confirmations referencing this Agreement ("Order Forms"). If you are accessing or using the Service on behalf of your company, you represent that you are authorized to accept this Agreement on behalf of your company, and all references to "you" or "Customer" reference your company.</p>
          
          <p>The "Effective Date" of this Agreement is the date which is the earlier of (a) Customer's initial access to the Service through any online provisioning, registration or order process or (b) the effective date of the first Order Form referencing this Agreement.</p>
          
          <p className="legal-highlight">BY INDICATING YOUR ACCEPTANCE OF THIS AGREEMENT OR ACCESSING OR USING THE SERVICE, YOU ARE AGREEING TO BE BOUND BY ALL TERMS, CONDITIONS, AND NOTICES CONTAINED OR REFERENCED IN THIS AGREEMENT. IF YOU DO NOT AGREE TO THIS AGREEMENT, PLEASE DO NOT USE THE SERVICE.</p>

          <h2>1. DashFox One Service</h2>
          
          <h3>1.1 Service Description</h3>
          <p>DashFox One provides an AI-powered relationship management platform designed to help professionals maintain meaningful connections with their contacts at scale. The Service includes:</p>
          <ul>
            <li>Email Inbox Analysis: Automated scanning and analysis of Customer's email history to identify contacts worth nurturing</li>
            <li>Contact Discovery and Categorization: AI-powered identification and classification of contacts (e.g., past clients, prospects, professional relationships)</li>
            <li>Relationship Intelligence Extraction: Structured extraction of relationship data including personal information, transaction history, and communication patterns</li>
            <li>Personalized Message Generation: AI-generated, personalized follow-up messages based on relationship history and context</li>
            <li>Message Review and Approval Workflow: Weekly batches of pre-written messages for Customer review, editing, and approval</li>
            <li>Direct Email Delivery: Sending approved messages directly from Customer's email account</li>
            <li>Data Export Capabilities: Structured export of relationship data in usable formats</li>
          </ul>
          <p>(collectively, the "Service" or "Services")</p>

          <h3>1.2 Access and Use</h3>
          <p>Customer may access and use the Service for its business operations, subject to this Agreement and any scope of use restrictions on the applicable Order Form. Access is limited to Customer and its authorized employees and contractors acting solely for Customer's benefit ("Permitted Users").</p>
          
          <p>To use the Service, Customer must:</p>
          <ul>
            <li>Connect their email account(s) via OAuth authentication</li>
            <li>Allow read access to analyze email history and write access to send approved messages</li>
            <li>Register for a DashFox One account with accurate, current, and complete information</li>
          </ul>
          <p>Customer is responsible for maintaining the confidentiality of account credentials and for all activities under its account.</p>

          <h3>1.3 Use Restrictions</h3>
          <p>Customer shall not:</p>
          <ul>
            <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations</li>
            <li>Attempt to gain unauthorized access to any portion of the Service or its related systems</li>
            <li>Use the Service to send spam, unsolicited communications, or messages that violate applicable anti-spam laws</li>
            <li>Resell, sublicense, or provide the Service to third parties without DashFox One's written consent</li>
            <li>Use the Service to process email accounts belonging to individuals other than Customer or Customer's authorized business accounts</li>
          </ul>

          <h2>2. Service Plans and Fees</h2>
          
          <h3>2.1 Service Components</h3>
          <p>The Service consists of two fee components:</p>
          <p><strong>a) Inbox Scanning Fee (One-time):</strong></p>
          <ul>
            <li>Charged based on the volume of email history to be scanned</li>
            <li>Pricing determined by years of history analyzed</li>
            <li>May be capped at a maximum amount regardless of history length</li>
            <li>Covers analysis of multiple email accounts if applicable</li>
            <li>Includes data extraction and initial setup</li>
          </ul>
          <p><strong>b) Subscription Fee (Recurring):</strong></p>
          <ul>
            <li>Ongoing access to the Service charged on a recurring basis</li>
            <li>Billing frequency as specified in the applicable Order Form</li>
            <li>Promotional, referral, or team rates may apply as specified in Order Forms</li>
          </ul>

          <h3>2.2 Pricing</h3>
          <p>Current pricing is set forth in the applicable Order Form or as communicated during the registration process. DashFox One reserves the right to modify pricing with thirty (30) days' advance notice to Customer. Pricing changes will take effect at the next renewal period following the notice period.</p>

          <h3>2.3 Payment Terms</h3>
          <p>Payment for the Inbox Scanning Fee is due upon initiation of the scan. Subscription Fees are due as specified in the Order Form. All fees are non-refundable except as expressly provided in this Agreement.</p>
          <p>Customer is responsible for all taxes, levies, or duties imposed by taxing authorities (excluding taxes on DashFox One's income). Late payments are subject to a service charge of 1.5% per month or the maximum allowed by law, whichever is less.</p>

          <h3>2.4 Subscription Term and Renewal</h3>
          <p>Subscription terms and renewal periods are as specified in the applicable Order Form. Unless otherwise specified, subscriptions automatically renew for successive terms of the same length unless either party provides written notice of non-renewal at least thirty (30) days prior to the end of the current term.</p>

          <h2>3. Data Processing and Privacy</h2>
          
          <h3>3.1 Email Data Processing</h3>
          <p>DashFox One processes Customer's email data solely to provide the Service. This processing includes:</p>
          <ul>
            <li>Automated scanning of email history using AI/machine learning models</li>
            <li>Extraction of contact information and relationship context</li>
            <li>Categorization of contacts and relationships</li>
            <li>Generation of personalized messages based on extracted data</li>
          </ul>

          <h3>3.2 Data Storage and Security</h3>
          <ul>
            <li>No Raw Email Storage: DashFox One does not store raw email content on its servers after processing</li>
            <li>Structured Data Only: Only extracted, structured relationship summaries are retained</li>
            <li>OAuth Authentication: Email access uses industry-standard OAuth 2.0 protocols - DashFox One never sees or stores email passwords</li>
            <li>Encryption: All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
            <li>Access Controls: No DashFox One personnel have access to Customer email content under normal operations</li>
          </ul>

          <h3>3.3 Data Ownership and Control</h3>
          <ul>
            <li>Customer retains all ownership rights to their data</li>
            <li>Customer can revoke email access at any time through OAuth controls</li>
            <li>Customer can export their relationship data at any time</li>
            <li>Upon termination, Customer data may be deleted after a reasonable retention period</li>
          </ul>

          <h3>3.4 Compliance</h3>
          <p>DashFox One undergoes annual security audits as required by Google and Microsoft for email integration partners. For financial services customers, DashFox One maintains compliance with applicable SEC and FINRA requirements for data security.</p>

          <h2>4. Intellectual Property</h2>
          
          <h3>4.1 Customer Data</h3>
          <p>Customer retains all right, title, and interest in Customer Data. DashFox is granted a limited license to process Customer Data solely to provide the Service.</p>

          <h3>4.2 DashFox One Technology</h3>
          <p>DashFox One retains all rights to the Service, including all software, algorithms, AI models, and technology used to provide the Service. No rights are granted except as expressly stated in this Agreement.</p>

          <h3>4.3 Aggregated Data</h3>
          <p>DashFox One may collect and use aggregated, anonymized data about Service usage for improvement, development, and analytical purposes, provided such data cannot be used to identify Customer or any individual.</p>

          <h2>5. Professional Services Disclaimer</h2>
          
          <h3>5.1 Not Professional Advice</h3>
          <p>The Service is a technology tool for relationship management and does not constitute:</p>
          <ul>
            <li>Investment, financial, or tax advice</li>
            <li>Legal advice or services</li>
            <li>Real estate brokerage services</li>
            <li>Any form of professional consulting</li>
          </ul>

          <h3>5.2 Customer Responsibility</h3>
          <p>Customer acknowledges that:</p>
          <ul>
            <li>Customer is solely responsible for all communications sent through the Service</li>
            <li>Customer must review and approve all messages before sending</li>
            <li>Customer is responsible for compliance with all applicable professional regulations</li>
            <li>Customer must maintain appropriate professional licenses for their business activities</li>
          </ul>

          <h2>6. Confidentiality</h2>
          
          <h3>6.1 Mutual Obligations</h3>
          <p>Each party shall maintain the confidentiality of the other party's Confidential Information and shall not use such Confidential Information except to perform its obligations under this Agreement.</p>

          <h3>6.2 Exceptions</h3>
          <p>Confidentiality obligations do not apply to information that:</p>
          <ul>
            <li>Is or becomes publicly available through no breach by the receiving party</li>
            <li>Was rightfully known or independently developed without use of Confidential Information</li>
            <li>Is required to be disclosed by law (with prompt notice to the disclosing party where permitted)</li>
          </ul>

          <h2>7. Warranties and Disclaimers</h2>
          
          <h3>7.1 Service Availability</h3>
          <p>DashFox One will use commercially reasonable efforts to maintain Service availability but does not guarantee uninterrupted or error-free operation.</p>

          <h3>7.2 Disclaimer</h3>
          <p className="legal-highlight">EXCEPT AS EXPRESSLY PROVIDED HEREIN, THE SERVICE IS PROVIDED "AS IS" AND DASHFOX ONE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. DASHFOX ONE DOES NOT WARRANT THE ACCURACY OR COMPLETENESS OF AI-GENERATED CONTENT.</p>

          <h2>8. Limitation of Liability</h2>
          <p className="legal-highlight">TO THE MAXIMUM EXTENT PERMITTED BY LAW, DASHFOX ONE'S TOTAL LIABILITY SHALL NOT EXCEED THE FEES PAID BY CUSTOMER IN THE TWELVE MONTHS PRECEDING THE CLAIM. IN NO EVENT SHALL DASHFOX ONE BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.</p>

          <h2>9. Indemnification</h2>
          <p>Customer shall indemnify and hold DashFox One harmless from any claims arising from:</p>
          <ul>
            <li>Customer's use of the Service</li>
            <li>Content of messages sent through the Service</li>
            <li>Violation of applicable laws or regulations</li>
            <li>Breach of this Agreement</li>
          </ul>

          <h2>10. Term and Termination</h2>
          
          <h3>10.1 Term</h3>
          <p>This Agreement continues until terminated by either party.</p>

          <h3>10.2 Termination</h3>
          <p>Either party may terminate:</p>
          <ul>
            <li>For convenience with thirty (30) days' written notice</li>
            <li>Immediately for material breach that remains uncured after thirty (30) days' notice</li>
            <li>Immediately for non-payment</li>
          </ul>

          <h3>10.3 Effect of Termination</h3>
          <p>Upon termination:</p>
          <ul>
            <li>Customer's access to the Service ceases immediately</li>
            <li>Customer shall pay all outstanding fees</li>
            <li>Customer may request data export within 30 days</li>
            <li>Sections that by their nature should survive termination shall remain in effect</li>
          </ul>

          <h2>11. General Provisions</h2>
          
          <h3>11.1 Governing Law</h3>
          <p>This Agreement is governed by the laws of the State of New York, without regard to conflict of law principles.</p>

          <h3>11.2 Entire Agreement</h3>
          <p>This Agreement constitutes the entire agreement between the parties and supersedes all prior agreements relating to the subject matter.</p>

          <h3>11.3 Modifications</h3>
          <p>DashFox One may modify this Agreement with thirty (30) days' notice. Continued use after the effective date constitutes acceptance of modifications.</p>

          <h3>11.4 Assignment</h3>
          <p>Customer may not assign this Agreement without DashFox One's written consent. DashFox One may assign this Agreement without restriction.</p>

          <h3>11.5 Severability</h3>
          <p>If any provision is found unenforceable, the remainder of the Agreement continues in full force and effect.</p>

          <h3>11.6 Notices</h3>
          <p>All notices must be in writing and are effective when delivered electronically or by certified mail.</p>

          <p className="legal-copyright">© 2024 DashFox One, Inc. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;